import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Plus, Edit2, Trash2, Layers, MoreVertical, X, Sparkles, Sun, MessageSquare, Image as ImageIcon } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function CategoryManagement() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: "", type: "Festival", status: "Active", image: "", thumbnail: "", index: 1 });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedThumbnails, setSelectedThumbnails] = useState([]);
  const [imageNamesInput, setImageNamesInput] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getColorForType = (type) => {
    switch (type) {
      case "Festival": return "bg-pink-50 text-pink-600 border-pink-100";
      case "Darshan": return "bg-orange-50 text-orange-600 border-orange-100";
      case "Suvichar": return "bg-blue-50 text-blue-600 border-blue-100";
      case "Template": return "bg-purple-50 text-purple-600 border-purple-100";
      default: return "bg-slate-50 text-slate-600 border-slate-100 dark:border-slate-700";
    }
  };

  const getCategoryType = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes("festival")) return "Festival";
    if (nameLower.includes("darshan")) return "Darshan";
    if (nameLower.includes("suvichar") || nameLower.includes("quote")) return "Suvichar";
    return "Template";
  };

  const fetchCategories = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/categories/all-categories`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch categories");
        }
        return res.json();
      })
      .then((resJson) => {
        if (resJson && resJson.status && resJson.data) {
          const mapped = resJson.data.map((cat) => {
            const type = getCategoryType(cat.category_name);
            return {
              id: cat.category_id_PK,
              name: cat.category_name,
              index: cat.category_index !== undefined && cat.category_index !== null ? Number(cat.category_index) : 0,
              type: type,
              status: "Active",
              itemCount: 0,
              color: getColorForType(type),
              image: cat.category_image,
              imageNames: cat.category_image_names ? cat.category_image_names.split(',') : [],
              thumbnail: cat.category_thumbnail
            };
          });
          setCategories(mapped);
        } else {
          throw new Error(resJson.message || "Invalid response format");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Categories fetch error:", err);
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const getIconForType = (type) => {
    switch (type) {
      case "Festival": return <Sparkles size={24} />;
      case "Darshan": return <Sun size={24} />;
      case "Suvichar": return <MessageSquare size={24} />;
      case "Template": return <Layers size={24} />;
      default: return <Layers size={24} />;
    }
  };

  const handleSaveCategory = () => {
    if (!newCategory.name.trim()) {
      Swal.fire("Error", "Category Name is required!", "error");
      return;
    }

    if (editingCategoryId) {
      const formData = new FormData();
      formData.append("category_name", newCategory.name);
      formData.append("category_index", newCategory.index !== undefined && newCategory.index !== null && newCategory.index !== "" ? newCategory.index : 0);

      Swal.fire({
        title: "Updating...",
        text: "Please wait while the category is updated.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      fetch(`${API_BASE_URL}/api/categories/update-category/${editingCategoryId}`, {
        method: "PUT",
        body: formData
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to update category");
          }
          return res.json();
        })
        .then((resJson) => {
          Swal.close();
          if (resJson && resJson.status) {
            Swal.fire("Success", "Category updated successfully!", "success");
            fetchCategories();
            setNewCategory({ name: "", type: "Festival", status: "Active", image: "", thumbnail: "", index: 1 });
            setSelectedFiles([]);
            setSelectedThumbnails([]);
            setImageNamesInput([]);
            setEditingCategoryId(null);
            setIsCreateModalOpen(false);
          } else {
            throw new Error(resJson.message || "Failed to update category");
          }
        })
        .catch((err) => {
          Swal.close();
          console.error("Update category error:", err);
          Swal.fire("Error", err.message, "error");
        });
    } else {
      const formData = new FormData();
      formData.append("category_name", newCategory.name);
      formData.append("category_index", newCategory.index !== undefined && newCategory.index !== null && newCategory.index !== "" ? newCategory.index : 0);

      Swal.fire({
        title: "Creating...",
        text: "Please wait while the category is created.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      fetch(`${API_BASE_URL}/api/categories/create-category`, {
        method: "POST",
        body: formData
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to create category");
          }
          return res.json();
        })
        .then((resJson) => {
          Swal.close();
          if (resJson && resJson.status) {
            Swal.fire("Success", "Category created successfully!", "success");
            fetchCategories();
            setNewCategory({ name: "", type: "Festival", status: "Active", image: "", thumbnail: "", index: 1 });
            setSelectedFiles([]);
            setSelectedThumbnails([]);
            setImageNamesInput([]);
            setEditingCategoryId(null);
            setIsCreateModalOpen(false);
          } else {
            throw new Error(resJson.message || "Failed to create category");
          }
        })
        .catch((err) => {
          Swal.close();
          console.error("Create category error:", err);
          Swal.fire("Error", err.message, "error");
        });
    }
  };

  const handleEditCategory = (category) => {
    setNewCategory({
      name: category.name,
      type: category.type,
      status: category.status,
      image: category.image || "",
      thumbnail: category.thumbnail || "",
      index: category.index !== undefined && category.index !== null ? category.index : 0
    });
    setEditingCategoryId(category.id);
    setSelectedFiles([]);
    setSelectedThumbnails([]);
    setImageNamesInput([]);
    setIsCreateModalOpen(true);
  };

  const handleDeleteCategory = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while the category is deleted.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        fetch(`${API_BASE_URL}/api/categories/delete-category/${id}`, {
          method: "DELETE"
        })
          .then((res) => {
            if (!res.ok) {
              throw new Error("Failed to delete category");
            }
            return res.json();
          })
          .then((resJson) => {
            Swal.close();
            if (resJson && resJson.status) {
              Swal.fire('Deleted!', 'Category has been deleted.', 'success');
              fetchCategories();
            } else {
              throw new Error(resJson.message || "Failed to delete category");
            }
          })
          .catch((err) => {
            Swal.close();
            console.error("Delete category error:", err);
            Swal.fire("Error", err.message, "error");
          });
      }
    });
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Category Management"
          subtitle="Manage sections like Festivals, Darshan, and Templates."
        />

        <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row lg:items-center justify-between gap-4">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white dark:bg-slate-800/80 backdrop-blur-md border border-white dark:border-slate-700 shadow-[0_4px_20px_rgba(0,0,0,0.03)] rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder-slate-400 dark:text-slate-100"
              />
            </div>

            <div className="flex items-center gap-3 w-full lg:w-auto">
              <button
                onClick={() => {
                  setNewCategory({ name: "", type: "Festival", status: "Active", image: "", thumbnail: "", index: 1 });
                  setEditingCategoryId(null);
                  setSelectedThumbnails([]);
                  setSelectedFiles([]);
                  setImageNamesInput([]);
                  setIsCreateModalOpen(true);
                }}
                className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2.5 rounded-xl text-sm font-medium hover:-translate-y-0.5 transition-all flex items-center gap-2">
                <Plus size={16} />
                Create Category
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-sm font-medium">
              Error loading categories: {error}. Please try again later.
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12 flex-1">
              <div className="text-slate-500 dark:text-slate-400 font-medium animate-pulse">Loading categories...</div>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex items-center justify-center py-12 flex-1">
              <div className="text-slate-500 dark:text-slate-400 font-medium">No categories found.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 overflow-y-auto pb-8">
              {categories
                .filter((category) => {
                  const matchCategory = category.name.toLowerCase().includes(searchTerm.toLowerCase());
                  const matchImages = category.imageNames && category.imageNames.some(name => name.trim().toLowerCase().includes(searchTerm.toLowerCase()));
                  return matchCategory || matchImages;
                })
                .map((category) => {
                  const showAllImages = searchTerm === "" || category.name.toLowerCase().includes(searchTerm.toLowerCase());
                  return (
                    <div key={category.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col hover:shadow-md transition-all group">

                      <div className="flex justify-between items-start mb-4">
                        <div className="flex gap-2 max-w-[70%] overflow-x-auto pb-2 custom-scrollbar">
                          <div className={`w-16 h-16 min-w-[4rem] rounded-xl flex items-center justify-center border shadow-sm ${category.color} flex-shrink-0`}>
                            {getIconForType(category.type)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800" title="Display Order Index">
                            Index: {category.index ?? 0}
                          </span>
                          <button className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100">
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-snug mb-1 group-hover:text-blue-600 transition-colors">
                        {category.name}
                      </h3>

                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-6">
                        {category.type}
                      </span>

                      <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-sm">
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-400 mb-0.5">Items</span>
                          <span className="font-medium text-slate-700">{category.itemCount}</span>
                        </div>

                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wide uppercase ${category.status === 'Active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-slate-100 text-slate-600 border border-slate-200 dark:border-slate-700'}`}>
                          {category.status}
                        </span>
                      </div>

                      {/* Hover Actions */}
                      <div className="mt-4 flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-700/60">
                        <button onClick={() => handleEditCategory(category)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 hover:text-emerald-600 rounded-lg transition-colors">
                          <Edit2 size={16} /> Edit
                        </button>
                        <button onClick={() => handleDeleteCategory(category.id)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors">
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>

                    </div>
                  )
                })}
            </div>
          )}

        </div>
      </div>

      {/* Create Category Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xl w-full max-w-md relative z-10 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-700">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{editingCategoryId ? 'Edit Category' : 'Create New Category'}</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Category Name</label>
                <input
                  type="text"
                  placeholder="e.g. National Festivals"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder-slate-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                  Category Index / Display Order <span className="text-xs text-slate-400 font-normal">(1, 2, 3...)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 1, 2, 3"
                  value={newCategory.index}
                  onChange={(e) => setNewCategory({ ...newCategory, index: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="w-full bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder-slate-400"
                />
                <p className="text-xs text-slate-400 mt-1">Priority order: 1 appears first, 2 appears second, etc.</p>
              </div>

            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 bg-slate-100 dark:bg-slate-800/80 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm hover:shadow-md"
              >
                {editingCategoryId ? 'Save Changes' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
